import { nearestIndex } from '../lib/datalog';
import type {
  CursorResult,
  LoadOptions,
  LogSessionMetadata,
  RangeStatistics,
  RenderWindowResult,
  TimeRange,
  WorkerError,
  WorkerRequest,
  WorkerResponse,
} from '../types';

type ProgressCallback = (progress: number, phase: string) => void;
type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (reason: DatalogClientError) => void;
  progress?: ProgressCallback;
};

export class DatalogClientError extends Error {
  readonly code: string;
  readonly candidates: WorkerError['candidates'];

  constructor(error: WorkerError) {
    super(error.message);
    this.name = 'DatalogClientError';
    this.code = error.code;
    this.candidates = error.candidates;
  }
}

class DatalogClient {
  private readonly worker = new Worker(new URL('../workers/datalog.worker.ts', import.meta.url), {
    type: 'module',
  });

  private readonly pending = new Map<string, PendingRequest>();
  private timeValues: Float64Array<ArrayBufferLike> = new Float64Array();
  private activeLoadRequestId: string | null = null;

  constructor() {
    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      const pending = this.pending.get(response.requestId);
      if (!pending) return;
      if (response.type === 'progress') {
        pending.progress?.(response.progress, response.phase);
        return;
      }
      this.pending.delete(response.requestId);
      if (response.type === 'error') {
        pending.reject(new DatalogClientError(response.error));
        return;
      }
      if (response.type === 'loadComplete') {
        this.activeLoadRequestId = null;
        this.timeValues = response.timeValues;
        pending.resolve(response.metadata);
      } else if (response.type === 'renderComplete') pending.resolve(response.result);
      else if (response.type === 'cursorComplete') pending.resolve(response.result);
      else if (response.type === 'statsComplete') pending.resolve(response.result);
      else if (response.type === 'exportComplete') pending.resolve({ blob: response.blob, fileName: response.fileName });
    };
    this.worker.onerror = (event) => {
      const error = new DatalogClientError({
        code: 'WORKER_ERROR',
        message: event.message || 'The local analysis worker stopped unexpectedly.',
      });
      for (const pending of this.pending.values()) pending.reject(error);
      this.pending.clear();
      this.activeLoadRequestId = null;
    };
    this.worker.onmessageerror = () => {
      const error = new DatalogClientError({
        code: 'WORKER_MESSAGE_ERROR',
        message: 'The browser could not read a local analysis result.',
      });
      for (const pending of this.pending.values()) pending.reject(error);
      this.pending.clear();
    };
  }

  private request<T>(request: WorkerRequest, progress?: ProgressCallback): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.pending.set(request.requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        progress,
      });
      this.worker.postMessage(request);
    });
  }

  async loadFile(file: File, options?: LoadOptions, progress?: ProgressCallback): Promise<LogSessionMetadata> {
    // A newer load supersedes any in-flight one; otherwise the older parse can finish last and win.
    this.cancelLoad();
    const requestId = crypto.randomUUID();
    this.activeLoadRequestId = requestId;
    return this.request<LogSessionMetadata>({ type: 'load', requestId, file, options }, progress);
  }

  cancelLoad(): void {
    if (!this.activeLoadRequestId) return;
    this.worker.postMessage({ type: 'cancel', requestId: this.activeLoadRequestId } satisfies WorkerRequest);
    const pending = this.pending.get(this.activeLoadRequestId);
    pending?.reject(new DatalogClientError({ code: 'CANCELED', message: 'Import canceled.' }));
    this.pending.delete(this.activeLoadRequestId);
    this.activeLoadRequestId = null;
  }

  renderWindow(channelIds: string[], range: TimeRange, pixelWidth: number): Promise<RenderWindowResult> {
    return this.request({
      type: 'renderWindow',
      requestId: crypto.randomUUID(),
      channelIds,
      range,
      pixelWidth,
    });
  }

  cursorValues(time: number, channelIds: string[]): Promise<CursorResult> {
    return this.request({
      type: 'cursorValues',
      requestId: crypto.randomUUID(),
      time,
      channelIds,
    });
  }

  rangeStatistics(range: TimeRange, channelIds: string[]): Promise<RangeStatistics[]> {
    return this.request({
      type: 'rangeStats',
      requestId: crypto.randomUUID(),
      range,
      channelIds,
    });
  }

  exportRange(range: TimeRange): Promise<{ blob: Blob; fileName: string }> {
    return this.request({ type: 'exportRange', requestId: crypto.randomUUID(), range });
  }

  nearestTime(time: number): number {
    if (!this.timeValues.length) return time;
    return this.timeValues[nearestIndex(this.timeValues, time)];
  }

  stepTime(time: number, step: number): number {
    if (!this.timeValues.length) return time;
    const index = nearestIndex(this.timeValues, time);
    const next = Math.max(0, Math.min(this.timeValues.length - 1, index + step));
    return this.timeValues[next];
  }
}

export const datalogClient = new DatalogClient();
